import { Controller } from 'stimulus';
import debounce from '../helpers/debounce';

class SearchBar extends Controller {
  title = 'SearchBar';

  // eslint-disable-next-line class-methods-use-this
  connect() {
    this.isSearching = false;

    SearchBar.loadTagify().then((Tagify) => {
      this.tagify = new Tagify(this.tagsTarget, {
        whitelist: [],
        dropdown: {
          enabled: 0,
          maxItems: 5,
          position: 'text',
          closeOnSelect: false,
          highlightFirst: true,
        },
      });

      this.onDebouncedInput = debounce(this.onTagifyInput.bind(this), 250);

      if (typeof this.tagify.on === 'function') {
        this.tagify.on('change', this.onTagifyChange.bind(this));
      }

      if (typeof this.tagify.on === 'function') {
        this.tagify.on('input', this.onDebouncedInput);
      }

      if (this.data.get('autofocus') === 'true') {
        const input = this.element.parentNode.querySelector('.tagify__input');

        if (input) {
          setTimeout(() => {
            input.focus();
          }, 100);
        }
      }

      this.restoreTags();
    });
  }

  disconnect() {
    if (this.tagify) {
      this.tagify.destroy();
    }
  }

  restoreTags() {
    if (!this.hasInputTarget) {
      return;
    }

    const { value } = this.inputTarget;

    // Check that the values seems like an array
    if (value.indexOf('[') !== 0) {
      return;
    }

    const tags = JSON.parse(value);

    tags.forEach((tag) => {
      this.tagify.addTags([tag]);
    });
  }

  reset() {
    this.tagify.removeAllTags();
  }

  onTagifyInput(event) {
    const { value } = event.detail;

    if (event.type === 'change') {
      return;
    }

    SearchBar.handleQuerySideEffects(value);

    if (value.length >= 3) {
      this.trackSearch(value);
    }

    if (!this.data.has('autocomplete-api')) {
      return;
    }

    this.tagify.settings.whitelist.length = 0;

    if (value.length < 3) {
      return;
    }

    this.tagify.loading(true).dropdown.hide.call(this.tagify);

    this.autocomplete(value)
      .then((res) => {
        this.tagify.settings.whitelist.splice(0, res.length, ...res);
        this.tagify.loading(false).dropdown.show.call(this.tagify, value);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
        this.tagify.loading(false).dropdown.hide.call(this.tagify);
      });
  }

  onTagifyChange() {
    const { value } = this.tagify;

    this.storeQuery();

    const customEvent = new CustomEvent('tags-change', {
      detail: value,
    });

    this.element.dispatchEvent(customEvent);
  }

  static handleQuerySideEffects(value) {
    if (!value) {
      return;
    }

    if (value === 'console') {
      window.location.href = window.app.settings.consoleUrl;
    } else if (value === 'pong') {
      window.location.href = window.app.settings.pongUrl;
    }
  }

  storeQuery() {
    const { value } = this.tagify.DOM.originalInput;

    if (this.hasInputTarget) {
      this.inputTarget.value = value;
    }
  }

  autocomplete(query) {
    return new Promise((resolve) => {
      const params = new URLSearchParams();
      params.append('q', query);

      const api = this.data.get('autocomplete-api');

      fetch(`${api}?${params}`)
        .then((res) => res.json())
        .then((res) => {
          resolve(res);
        });
    });
  }

  // eslint-disable-next-line class-methods-use-this
  trackSearch(query) {
    if (typeof window.dataLayer !== 'undefined') {
      const action = window.location.pathname
        .replace(/[^a-zA-Z0-9-]+/g, '-')
        .replace(/^-nl-/, '')
        .replace(/^-be-/, '');

      window.dataLayer.push({
        event: 'ga_event',
        ga_event_category: 'search_bar',
        ga_event_action: action,
        ga_event_label: query,
      });
    }
  }

  static loadTagify() {
    return import(/* webpackChunkName: "tagify" */ '@yaireo/tagify')
      .then((module) => module.default);
  }
}

SearchBar.targets = [
  'tags',
  'input',
];

export default SearchBar;
